import React, {useEffect, useState} from 'react';
import {Alert} from '@betareader/design-system';
import {withTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {authUtil} from '../../../utils';

const settingsUrl = bookId =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/notification-settings?kind=book&b=${bookId}`;

export const BookNotificationSettings = ({t}) => {
  const bookId = useSelector(state => state.book.currentBook?._id);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!bookId) return undefined;
    let cancelled = false;
    setSettings(null);
    setError(false);
    const load = async () => {
      try {
        const idToken = await authUtil.getFreshIdToken();
        const response = await fetch(settingsUrl(bookId), {
          headers: {'Content-Type': 'application/json', 'br-token': idToken},
        });
        if (!response.ok) throw new Error('Could not load notification settings');
        const loaded = await response.json();
        if (!cancelled) setSettings(loaded);
      } catch (cause) {
        if (!cancelled) setError(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [bookId]);

  const updateSetting = async (kind, checked) => {
    if (!bookId || !settings || saving) return;
    const updated = {
      ...settings,
      [kind]: {...settings[kind], email: checked},
    };
    setSaving(true);
    setError(false);
    try {
      const idToken = await authUtil.getFreshIdToken();
      const response = await fetch(settingsUrl(bookId), {
        method: 'PUT',
        headers: {'Content-Type': 'application/json', 'br-token': idToken},
        body: JSON.stringify(updated),
      });
      if (!response.ok) throw new Error('Could not save notification settings');
      setSettings(await response.json());
    } catch (cause) {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <fieldset aria-busy={saving} style={{border: 0, margin: 0, padding: 0}}>
      <legend>{t('Notifications')}</legend>
      {error && <Alert variant='error'>{t('SomethingWentWrong')}</Alert>}
      <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem'}}>
        <input
          type='checkbox'
          checked={Boolean(settings?.onComment?.email)}
          disabled={!settings || saving}
          onChange={event => updateSetting('onComment', event.target.checked)}
        />
        {t('SendEmailOnComments')}
      </label>
      <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem'}}>
        <input
          type='checkbox'
          checked={Boolean(settings?.onSurveyCompletion?.email)}
          disabled={!settings || saving}
          onChange={event => updateSetting('onSurveyCompletion', event.target.checked)}
        />
        {t('SendEmailOnSurveyComplete')}
      </label>
    </fieldset>
  );
};

export default withTranslation()(BookNotificationSettings);
